import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';

const AadhaarVerificationScreen = ({ verificationStatus, onStatusUpdate }) => {
    const navigate = useNavigate();
    const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
    const userInfo = JSON.parse(localStorage.getItem('tempUserInfo') || '{}');

    const [documentName, setDocumentName] = useState(userInfo.name || '');
    const [frontImage, setFrontImage] = useState(null);
    const [frontPreview, setFrontPreview] = useState(null);
    const [backImage, setBackImage] = useState(null);
    const [backPreview, setBackPreview] = useState(null);
    const [loading, setLoading] = useState(false);

    const isVerified = verificationStatus?.aadhaar?.verified || false;
    const isPending =
        verificationStatus?.aadhaar?.pending ||
        verificationStatus?.aadhaar?.photoSubmitted ||
        false;
    const verifiedAt = verificationStatus?.aadhaar?.verifiedAt;

    const handleImageSelect = (file, setImage, setPreview) => {
        if (!file) return;
        if (file.size > 5 * 1024 * 1024) {
            toast.error('Image size should be less than 5MB');
            return;
        }
        setImage(file);
        const reader = new FileReader();
        reader.onload = (event) => setPreview(event.target.result);
        reader.readAsDataURL(file);
    };

    const convertToBase64 = (file) =>
        new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result);
            reader.onerror = (err) => reject(err);
        });

    const handleSubmit = async () => {
        if (!documentName.trim()) {
            toast.error('Please enter your full name');
            return;
        }
        if (!frontImage) {
            toast.error('Please upload the front side of your Aadhaar card');
            return;
        }

        setLoading(true);
        try {
            const frontImageBase64 = await convertToBase64(frontImage);
            let backImageBase64 = null;

            if (backImage) {
                backImageBase64 = await convertToBase64(backImage);
            }

            await axios.post(`${baseUrl}/idfy/upload-kyc-document`, {
                userId: userInfo.id,
                documentType: 'aadhaar',
                name: documentName.trim(),
                frontImageBase64,
                backImageBase64,
            });

            toast.success(
                'Aadhaar uploaded. An admin will review and approve your verification shortly.',
            );
            if (onStatusUpdate) onStatusUpdate();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Upload failed. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    if (!userInfo.id) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
                <div className="text-center">
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">Session Expired</h2>
                    <p className="text-gray-600 mb-4">Please login again to continue verification</p>
                    <button
                        onClick={() => navigate('/login')}
                        className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
                    >
                        Go to Login
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
            <div className="max-w-md w-full space-y-6">
                <div className="text-center">
                    <h2 className="text-3xl font-bold text-gray-900">Aadhaar Verification</h2>
                    <p className="mt-2 text-sm text-gray-600">
                        {isVerified
                            ? 'Your Aadhaar has been verified'
                            : isPending
                                ? 'Your document is under admin review'
                                : 'Upload a photo of your Aadhaar card'}
                    </p>
                </div>

                <div className="bg-white py-8 px-6 shadow-xl rounded-2xl">
                    {isVerified ? (
                        <div className="text-center p-6 bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl border border-green-200">
                            <div className="mx-auto h-16 w-16 flex items-center justify-center rounded-full bg-gradient-to-r from-green-500 to-emerald-600 shadow-lg mb-4">
                                <svg className="h-8 w-8 text-white" fill="currentColor" viewBox="0 0 20 20">
                                    <path
                                        fillRule="evenodd"
                                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                        clipRule="evenodd"
                                    />
                                </svg>
                            </div>
                            <h3 className="text-xl font-bold text-green-900 mb-2">Aadhaar Verified</h3>
                            <p className="text-sm text-gray-600 mb-2">
                                Verified on: {verifiedAt ? new Date(verifiedAt).toLocaleDateString() : 'Recently'}
                            </p>
                            <span className="inline-block px-3 py-1 bg-green-100 text-green-800 text-xs font-semibold rounded-full">
                                Approved
                            </span>
                        </div>
                    ) : isPending ? (
                        <div className="text-center p-6 bg-gradient-to-br from-amber-50 to-yellow-50 rounded-xl border border-amber-200">
                            <div className="mx-auto h-16 w-16 flex items-center justify-center rounded-full bg-gradient-to-r from-amber-400 to-yellow-500 shadow-lg mb-4">
                                <svg className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                                    />
                                </svg>
                            </div>
                            <h3 className="text-xl font-bold text-amber-900 mb-2">Under Admin Review</h3>
                            <p className="text-sm text-amber-800 mb-4">
                                Your Aadhaar document has been submitted. An admin will review and approve your verification shortly.
                            </p>
                            <span className="inline-block px-3 py-1 bg-amber-100 text-amber-800 text-xs font-semibold rounded-full">
                                Pending Approval
                            </span>
                        </div>
                    ) : (
                        <div className="space-y-5">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Full Name <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    value={documentName}
                                    onChange={(event) => setDocumentName(event.target.value)}
                                    placeholder="Enter your full name"
                                    className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Aadhaar Card - Front Side <span className="text-red-500">*</span>
                                </label>
                                <UploadBox
                                    preview={frontPreview}
                                    onSelect={(file) => handleImageSelect(file, setFrontImage, setFrontPreview)}
                                    onRemove={() => {
                                        setFrontImage(null);
                                        setFrontPreview(null);
                                    }}
                                    inputId="aadhaar-front"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Aadhaar Card - Back Side <span className="text-gray-400 text-xs">(Optional)</span>
                                </label>
                                <UploadBox
                                    preview={backPreview}
                                    onSelect={(file) => handleImageSelect(file, setBackImage, setBackPreview)}
                                    onRemove={() => {
                                        setBackImage(null);
                                        setBackPreview(null);
                                    }}
                                    inputId="aadhaar-back"
                                />
                            </div>

                            <button
                                onClick={handleSubmit}
                                disabled={loading || !frontImage || !documentName.trim()}
                                className="w-full bg-blue-600 text-white py-3 px-4 rounded-xl font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                            >
                                {loading ? (
                                    <span className="flex items-center justify-center">
                                        <svg className="animate-spin h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                        </svg>
                                        Uploading...
                                    </span>
                                ) : (
                                    'Submit for Review'
                                )}
                            </button>
                        </div>
                    )}
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <div className="flex items-start">
                        <svg className="w-5 h-5 text-blue-600 mt-0.5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path
                                fillRule="evenodd"
                                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                                clipRule="evenodd"
                            />
                        </svg>
                        <p className="text-xs text-blue-800">
                            {isVerified
                                ? 'Your Aadhaar is verified. You can also upload a PAN card as an alternative document.'
                                : isPending
                                    ? 'Your document has been received. Once the admin approves it, your account will be marked as verified.'
                                    : 'Upload a clear photo of your Aadhaar card and enter your name. No Aadhaar number entry is required.'}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

const UploadBox = ({ preview, onSelect, onRemove, inputId }) => (
    <div className="flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-lg hover:border-blue-400 transition-colors">
        <div className="space-y-1 text-center">
            {preview ? (
                <div className="mb-2">
                    <img src={preview} alt="Preview" className="mx-auto h-32 w-auto rounded-lg shadow-md object-contain" />
                    <button onClick={onRemove} className="mt-2 text-sm text-red-600 hover:text-red-500">
                        Remove
                    </button>
                </div>
            ) : (
                <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                    <path
                        d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                        strokeWidth={2}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
            )}
            {!preview && (
                <div className="flex text-sm text-gray-600 justify-center">
                    <label htmlFor={inputId} className="cursor-pointer font-medium text-blue-600 hover:text-blue-500">
                        <span>Upload photo</span>
                        <input
                            id={inputId}
                            type="file"
                            accept="image/*"
                            className="sr-only"
                            onChange={(event) => onSelect(event.target.files[0])}
                        />
                    </label>
                    <p className="pl-1">or drag and drop</p>
                </div>
            )}
            {!preview && <p className="text-xs text-gray-500">PNG, JPG up to 5MB</p>}
        </div>
    </div>
);

export default AadhaarVerificationScreen;
